import TextareaAutosize from 'react-textarea-autosize';
import { BottomSheet, Button, Confirm, Typography } from '@/ui-kit';
import styles from './form-message.module.scss';
import { ActivatedProfileIcon2, BannedUserIcon, CheckIcon, CloseOutlinedIcon, DisabledCheckIcon, ErrorNotificationIcon, HiddenProfileIcon, OkHandIcon, TelegramDisabledIcon, TelegramIcon, VerificationIcon, VerifiedAccountIcon } from '@/assets/icons';
import React, { useEffect, useState } from 'react';
import { EDIT_MODE, EDITING_FINISHED, REPLY_FINISHED, REPLY_MODE, useChat, } from '@/store/useChat';
import { Profile } from '@/types/profile';
import { useAuth } from '@/store/useAuth';
import WebApp from '@twa-dev/sdk';
import { useVerification } from '@/store/useVerification';
import { useActionCableCreate } from '@/hooks/use-action-cable';
import { SocketPayload } from '@/types/socket-payload';
import { jwtService } from '@/services/jwt.service';
import { useVibration } from '@/store/useVibration';
import { useClearChatWhithUser } from '@/hooks/use-clear-chat-whith-user';
import { bannedSubtitle } from '@/utils/banned';
import { routesApp } from '@/constans/routes';
import { callYaMetrika } from '@/utils/call-ya-metrika';
import { profilesApi } from '@/api';
import { useToast } from '@/store/useToast';
import { VerificationBottomsheet } from '@/components/verification-bottomsheet';
import { PROFILE_CHANNEL_RECEIVED } from '@/utils/events';

interface Props {
    profileChat: Profile | undefined,
    onReply?: (data: {
        reply_message_id: number | string
        message: string
    }) => void;
    onEdit?: (data: {
        message_id: number | string
        message: string,
    }) => void;
    onSend?: (data: any) => void;
    onFocus: () => void;
    onBlur: () => void;
    refetchMessages: () => void;
    onChange?: (e: string) => void;
    onSubmit?: () => void;
}
export const FormMessage: React.FC<Props> = ({
    profileChat,
    onReply,
    onEdit,
    onSend,
    onFocus,
    onBlur,
    refetchMessages,
    onChange,
    onSubmit,
}) => {
    const msg = useChat((state) => state.msg);
    const setMsg = useChat((state) => state.setMsg);
    const contextMsg = useChat((state) => state.contextMsg);
    const clearContextMsg = useChat((state) => state.clearContextMsg);
    const chatMode = useChat((state) => state.chatMode);
    const setChatMode = useChat((state) => state.setChatMode);
    const setStatus = useChat((state) => state.setStatus);
    const clearMenu = useChat((state) => state.clearMenu);
    const setContextMsg = useChat((state) => state.setContextMsg);
    const userData = useAuth((state) => state.getUser());
    const setProfile = useAuth((state) => state.setProfile);
    const [isVerificationModalOpen, setIsVerificationModalOpen] =
        useState(false);
        
    const onEnterPress = (e: any) => {
        if (e.keyCode === 13 && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const { isVibrationEnabled } =
        useVibration(state => state);

    const handleSubmit = (e: any) => {
        e.preventDefault();
        onSubmit?.()
        handleSend();
        if (isVibrationEnabled) {
            WebApp
                .HapticFeedback
                .impactOccurred('medium');
            // pass for not android
            navigator.vibrate?.(10)
        }
    };

    const isEditMode = chatMode === EDIT_MODE
    const isReplyMode = chatMode === REPLY_MODE

    const handleSend = () => {
        if (!msg) return;

        if (isEditMode) {
            const editedMsgId = contextMsg?.id
            if (editedMsgId)
                onEdit?.({
                    message_id: editedMsgId,
                    message: msg
                });
            setStatus(EDITING_FINISHED)
        } else if (isReplyMode) {
            const replyMsgId = contextMsg?.id
            if (replyMsgId)
                onReply?.({
                    reply_message_id: replyMsgId,
                    message: msg
                });
            setStatus(REPLY_FINISHED)
        } else
            onSend?.({
                message: msg, // Optional
            });

        setMsg('');
        
        // reset chat mode
        setChatMode(null);

        clearMenu();
    };

    const handleBlur = () => {
        // setTimeout(() => {
        //     window.scrollTo({ top: 0, behavior: 'smooth' });
        // }, 800);
    };

    const onFocusHandle = () => {
        onFocus();
        handleBlur();
    };

    const onBlurHandle = () => {
        onBlur();
        handleBlur();
    };

    const { hasPendingVerificationError, setHasPendingVerificationError } = useVerification((state) => state);

    const onProfileChannelReceived = (data: CustomEventInit) => {
        const payload = data.detail
        switch (payload.type) {
            case 'profile_verified':
                setIsVerificationModalOpen(false)
                setProfile({ ...userData, verification_status: 'verified' })
                setHasPendingVerificationError(false)
                break;
        }
    }
    
    useEffect(() => {
        window.addEventListener(PROFILE_CHANNEL_RECEIVED, onProfileChannelReceived)
        return () => {
            window.removeEventListener(PROFILE_CHANNEL_RECEIVED, onProfileChannelReceived)
        };
    }, []);

    const [isOpenConfirm, setIsOpenConfirm] = useState(false);
    const handleConfirm = async () => {
        if (!profileChat)
            return
        try {
            await clearChatWithUserAction(profileChat.id);
            setIsOpenConfirm(false);
        } catch (error) {
            console.error(error);
        }
    };

    const { mutateAsync: clearChatById, isPending: isLoadingClearChat } = useClearChatWhithUser();
    
    const { setMessages } = useChat(state => state);

    const clearChatWithUserAction = async (profileId: number | '') => {
        await clearChatById(profileId);
        refetchMessages?.();
        setMessages((_) => [])
    };

    const showToast =
        useToast((state) => state.show);

    const onActivateAccountClick = async () => {
        callYaMetrika('return_to_search');
        try {
            await profilesApi.activate();
            showToast({
                icon: (
                    <ActivatedProfileIcon2 />
                ),
                msg: 'Вы снова в поиске!'
            })
            setProfile({
                ...userData,
                active: true,
            });
        } catch (e) {
            showToast({
                icon: <ErrorNotificationIcon />,
                msg: 'Ошибка. Попробуйте ещё раз'
            })
        }
    }

    return (
        <div
            id="chat-send-form"
            className={styles.wrapper}>
            {
                isEditMode ?
                    <div className={styles.editMsgHeaderWrap}>
                        <div className={styles.editMsgHeader}>
                            <div className={styles.editMsgHeaderMain}>
                                <span className={styles.editMsgHeaderMainTitle}>
                                    Редактирование
                                </span>
                                <span className={styles.editMsgHeaderMainSubtitle}>
                                    {contextMsg?.content}
                                </span>
                            </div>
                            <Button
                                className={styles.editMsgHeaderCloseBtn}
                                onClick={() => {
                                    setChatMode(null)
                                    setMsg('')
                                    setContextMsg(null)
                                }}>
                                <CloseOutlinedIcon />
                            </Button>
                        </div>
                    </div>
                : isReplyMode ?
                    <div className={styles.editMsgHeaderWrap}>
                        <div className={styles.editMsgHeader}>
                            <div className={styles.editMsgHeaderMain}>
                                <span className={styles.editMsgHeaderMainTitle}>
                                    {
                                        [
                                            'Ваш ответ',
                                            profileChat?.public_name
                                        ]
                                            .filter(e => !!e)
                                            .join(': ')
                                    }
                                </span>
                                <span className={styles.editMsgHeaderMainSubtitle}>
                                    {contextMsg?.content}
                                </span>
                            </div>
                            <Button
                                className={styles.editMsgHeaderCloseBtn}
                                onClick={() => {
                                    setChatMode(null)
                                    setMsg('')
                                    clearMenu()
                                    clearContextMsg()
                                }}>
                                <CloseOutlinedIcon />
                            </Button>
                        </div>
                    </div>
                : <></>
            }
            {
                profileChat
                    ?.moderation_status
                    === 'banned' ?
                    <div className={styles.bannedBlock}>
                        <div className={styles.bannedBlockContent}>
                            <BannedUserIcon className={styles.bannedBlockContentIcon} width={24} height={24} />
                            <div className={styles.bannedBlockContentInfo}>
                                <Typography
                                    variant="h6"
                                    fontFamily="SFProText"
                                    fontWeight="semibold">
                                    Аккаунт заблокирован
                                </Typography>
                                <Typography
                                    variant="p"
                                    fontFamily="SFProText">
                                    {bannedSubtitle}
                                </Typography>
                            </div>
                        </div>
                        <Button
                            textColor={'black'}
                            className={styles.verificationMsgBtn}
                            onClick={() => setIsOpenConfirm(true)}>
                            <Typography
                                variant="p"
                                fontFamily="SFProText"
                                fontWeight="semibold">
                                Очистить чат
                            </Typography>
                        </Button>
                    </div>
                :
                    !profileChat?.active ||
                    !userData?.active
                ?
                    <div className={styles.bannedBlock}>
                        <div className={styles.bannedBlockContent}>
                            <HiddenProfileIcon
                                width={24}
                                height={23.53}
                                className={styles.bannedBlockContentIcon} />
                            <div className={styles.bannedBlockContentInfo}>
                                <Typography
                                    variant="h6"
                                    fontFamily="SFProText"
                                    fontWeight="semibold">
                                    {
                                        !userData?.active ?
                                            'Твой профиль скрыт'
                                        :
                                            'Аккаунт скрыт'
                                    }
                                </Typography>
                                <Typography
                                    variant="p"
                                    fontFamily="SFProText">
                                    Диалог станет доступен, как только профиль будет включён.
                                </Typography>
                            </div>
                        </div>
                        {
                            !userData?.active &&
                            <Button
                                textColor={'black'}
                                className={styles.verificationMsgBtn}
                                onClick={onActivateAccountClick}>
                                <Typography
                                    variant="p"
                                    fontFamily="SFProText"
                                    fontWeight="semibold">
                                    Включить профиль
                                </Typography>
                            </Button>
                        }
                    </div>
                :
                    userData
                        ?.verification_status
                        === 'pending' ||
                    userData
                        ?.verification_status
                        === 'rejected' ||
                    hasPendingVerificationError
                ?
                    <div className={styles.verificationMsg}>
                        <div className={styles.verificationMsgInfo}>
                            <div className={styles.verificationMsgInfoHeader}>
                                <Typography
                                    variant="h6"
                                    fontFamily="SFProText"
                                    fontWeight="semibold">
                                    Euphora
                                </Typography>
                                <VerifiedAccountIcon />
                            </div>
                            <div className={styles.verificationMsgInfoSubtitle}>
                                <Typography
                                    variant="p"
                                    fontFamily="SFProText">
                                    Чтобы продолжить общение, нужно пройти короткую верификацию. Это займёт пару минут и помогает нам сохранять безопасность и доверие в чате.
                                </Typography>
                            </div>
                        </div>
                        <Button
                            textColor={'black'}
                            className={styles.verificationMsgBtn}
                            onClick={() => setIsVerificationModalOpen(true)}>
                            <Typography
                                variant="p"
                                fontFamily="SFProText"
                                fontWeight="semibold">
                                Пройти верификацию
                            </Typography>
                        </Button>
                    </div>
                :
                    <form
                        onSubmit={handleSubmit}
                        className={styles.formMessage}>
                        <TextareaAutosize
                            minRows={1}
                            maxRows={4}
                            className={styles.textarea}
                            placeholder="Сообщение"
                            value={msg}
                            onKeyDown={onEnterPress}
                            onChange={(e) => {
                                setMsg(e.target.value)
                                onChange?.(e.target.value)

                            }}
                            onFocus={onFocusHandle}
                            onBlur={onBlurHandle} />
                        <Button
                            type="submit"
                            className={styles.button}
                            disabled={!msg.length}>
                            {
                                msg.length ?
                                    (
                                        chatMode === 'edit' ?
                                            <CheckIcon
                                                width={24}
                                                height={24} />
                                        :
                                            <TelegramIcon
                                                width={24}
                                                height={24} />
                                    )
                                :
                                    (
                                        chatMode === 'edit' ?
                                            <DisabledCheckIcon
                                                width={24}
                                                height={24} />
                                            :
                                            <TelegramDisabledIcon
                                                width={24}
                                                height={24} />
                                    )
                            }
                        </Button>
                    </form>
            }
            <VerificationBottomsheet isVerificationModalOpen={isVerificationModalOpen} setIsVerificationModalOpen={setIsVerificationModalOpen} />
            <Confirm
                isOpen={isOpenConfirm}
                onClose={() => setIsOpenConfirm(false)}
                title={'Вы действительно хотите очистить чат с пользователем?'}
                text={'Все старые сообщения пропадут, вы подтверждаете очистку чата?'}
                onConfirm={handleConfirm}
                buttonTextSubmit="Принять"
                isLoading={isLoadingClearChat} />
        </div>
    );
};
